"use client"

import { useEffect } from "react"
import { ErrorPageTemplate, ErrorTemplates } from "@/components/error-page-template"
import { Button } from "@/components/ui/button"
import { RefreshCw, AlertTriangle } from "lucide-react"

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log the critical error
    console.error('Global application error:', error)
  }, [error])

  const customActions = (
    <div className="space-y-4">
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
        <div className="flex items-center gap-2 text-destructive mb-2">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-medium">Critical Error</span>
        </div>
        <p className="text-sm text-muted-foreground">
          A critical error has occurred that prevented the application from loading properly. 
          This is likely a temporary issue.
        </p>
      </div>
      
      <div className="text-sm text-muted-foreground">
        <p className="font-medium mb-2">To resolve this issue:</p>
        <ul className="text-left space-y-1">
          <li>1. Try refreshing the page</li>
          <li>2. Clear your browser cache and cookies</li>
          <li>3. Restart your browser</li>
          <li>4. Contact support if the problem persists</li>
        </ul>
      </div>
      
      <Button onClick={reset} size="lg" className="flex items-center gap-2">
        <RefreshCw className="h-4 w-4" />
        Reload Application
      </Button>
      
      {error.digest && (
        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
          <p className="font-mono">Error ID: {error.digest}</p>
          <p className="mt-1">Please include this ID when contacting support.</p>
        </div>
      )}
    </div>
  )

  return (
    <html>
      <body>
        <ErrorPageTemplate
          code="CRITICAL"
          title="Application Error"
          description="A critical error has occurred and the application cannot load. Our team has been automatically notified."
          icon={AlertTriangle}
          iconColor="text-red-500"
          customActions={customActions}
          showBackButton={false}
        />
      </body>
    </html>
  )
} 